/**
 * My Booking Settings Page
 * Users can configure their availability for bookings
 */

import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AvailabilitySettings, BookingCalendar } from '@/components/booking';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, ExternalLink, Calendar, Settings } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

export default function BookingSettings() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();

  const bookingUrl = user?.id
    ? `${window.location.origin}/book/${user.id}?name=${encodeURIComponent(user.email || '')}`
    : '';
  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast({
      title: language === 'fr' ? 'Lien copié!' : 'Link copied!',
    });
  };

  const t = {
    title: language === 'fr' ? 'Réservation en ligne' : 'Online Booking',
    subtitle: language === 'fr' 
      ? 'Configurez vos disponibilités et partagez votre lien de réservation'
      : 'Configure your availability and share your booking link',
    yourLink: language === 'fr' ? 'Votre lien de réservation' : 'Your booking link',
    yourLinkDesc: language === 'fr' 
      ? 'Partagez ce lien pour permettre aux autres de réserver des rendez-vous avec vous'
      : 'Share this link to allow others to book meetings with you',
    copy: language === 'fr' ? 'Copier' : 'Copy',
    preview: language === 'fr' ? 'Aperçu' : 'Preview',
    settings: language === 'fr' ? 'Paramètres' : 'Settings',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Booking Link */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {t.yourLink}
            </CardTitle>
            <CardDescription>{t.yourLinkDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input value={bookingUrl} readOnly className="font-mono text-sm" />
              <Button variant="outline" onClick={copyLink}>
                <Copy className="w-4 h-4 mr-2" />
                {t.copy}
              </Button>
              <Button variant="outline" onClick={() => window.open(bookingUrl, '_blank')}>
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="settings" className="w-full">
          <TabsList>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              {t.settings}
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Calendar className="w-4 h-4" />
              {t.preview}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-4">
            <AvailabilitySettings />
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            {user?.id && (
              <BookingCalendar
                hostId={user.id}
                hostName={user.email || 'Host'}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
